// ConfirmPH Platform Legal & Compliance: shared client helpers.
//
// The vocabulary here mirrors backend/platform_legal/__init__.py exactly.
// The rule the UI has to carry through from the backend is that absence of
// data is never compliance: "not configured" is styled as an outstanding
// item, never as neutral or good, and only "verified" / "not applicable"
// ever read as satisfied.
import { api } from "./api";

export const PL = "/admin/platform-legal";
export const LEGAL = "/legal";

// ---------------------------------------------------------------------------
// Capabilities (must match platform_legal/rbac.py)
// ---------------------------------------------------------------------------
export const CAP = {
  view: "legal.view",
  identity: "legal.identity.write",
  documents: "legal.documents.write",
  publish: "legal.documents.publish",
  merchants: "legal.merchants.write",
  complaints: "legal.complaints.handle",
  safety: "legal.safety.triage",
  privacyView: "privacy.view",
  privacyRequests: "privacy.requests.handle",
  privacyPia: "privacy.pia.write",
  privacyBreach: "privacy.breach.handle",
  dpo: "privacy.dpo",
  legalRequests: "legal.requests.handle",
  vendors: "legal.vendors.write",
  corporate: "legal.corporate.write",
  calendar: "legal.calendar.write",
  ai: "legal.ai.governance",
  export: "legal.export",
  roles: "legal.roles.manage",
};

export function can(capabilities, ...needed) {
  const held = new Set(capabilities || []);
  return needed.every((c) => held.has(c));
}

export function canAny(capabilities, ...needed) {
  const held = new Set(capabilities || []);
  return needed.some((c) => held.has(c));
}

// ---------------------------------------------------------------------------
// Status presentation
// ---------------------------------------------------------------------------
export const STATUS_LABELS = {
  not_configured: "Not configured",
  action_required: "Action required",
  in_progress: "In progress",
  pending_external: "Pending (outside ConfirmPH)",
  recorded_not_verified: "Recorded, not verified",
  verified: "Verified",
  expired: "Expired",
  not_applicable: "Not applicable",
  review_required: "Review required",
};

const TONES = {
  // Green is reserved for facts someone stood behind with evidence.
  verified: "border-emerald-200 bg-emerald-50 text-emerald-800",
  not_applicable: "border-slate-200 bg-slate-50 text-slate-600",
  // Everything unknown reads as outstanding, never as neutral.
  not_configured: "border-amber-200 bg-amber-50 text-amber-800",
  action_required: "border-rose-200 bg-rose-50 text-rose-700",
  expired: "border-rose-200 bg-rose-50 text-rose-700",
  review_required: "border-rose-200 bg-rose-50 text-rose-700",
  in_progress: "border-sky-200 bg-sky-50 text-sky-800",
  pending_external: "border-violet-200 bg-violet-50 text-violet-800",
  recorded_not_verified: "border-amber-200 bg-amber-50 text-amber-800",
};

export function statusTone(status) {
  return TONES[status] || "border-slate-200 bg-slate-50 text-slate-700";
}

export function statusLabel(status) {
  return STATUS_LABELS[status] || humanise(status);
}

export function humanise(value) {
  if (!value) return "";
  return String(value)
    .replace(/[_.]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Queue/clock tones shared by complaints, safety reports, privacy requests
// and breach windows.
export function clockTone(state) {
  return {
    overdue: "border-rose-200 bg-rose-50 text-rose-700",
    window_elapsed: "border-rose-200 bg-rose-50 text-rose-700",
    due_12h: "border-rose-200 bg-rose-50 text-rose-700",
    due_24h: "border-amber-200 bg-amber-50 text-amber-800",
    due_soon: "border-amber-200 bg-amber-50 text-amber-800",
    on_track: "border-emerald-200 bg-emerald-50 text-emerald-800",
    notified: "border-emerald-200 bg-emerald-50 text-emerald-800",
    closed: "border-slate-200 bg-slate-50 text-slate-600",
    not_applicable: "border-slate-200 bg-slate-50 text-slate-600",
  }[state] || "border-slate-200 bg-slate-50 text-slate-700";
}

export function clockLabel(clock) {
  if (!clock) return "";
  const map = {
    overdue: "Overdue", due_24h: "Due in 24h", due_12h: "Due in 12h",
    due_soon: "Due soon", on_track: "On track", closed: "Closed",
    window_elapsed: "Window elapsed", notified: "Notified",
    not_applicable: "No window", unknown: "Unknown",
  };
  return map[clock.state] || humanise(clock.state);
}

export const LAUNCH_STATE_LABELS = {
  NOT_READY: "Not ready",
  ACTION_REQUIRED: "Action required",
  READY_FOR_STAGING: "Ready for staging",
  READY_FOR_PUBLIC_PRODUCTION: "Ready for public production",
};

export function launchTone(state) {
  return {
    READY_FOR_PUBLIC_PRODUCTION: "border-emerald-200 bg-emerald-50 text-emerald-800",
    READY_FOR_STAGING: "border-sky-200 bg-sky-50 text-sky-800",
    ACTION_REQUIRED: "border-amber-200 bg-amber-50 text-amber-800",
    NOT_READY: "border-rose-200 bg-rose-50 text-rose-700",
  }[state] || "border-slate-200 bg-slate-50 text-slate-700";
}

export const MERCHANT_STATE_TONES = {
  OPERATIONAL: "border-emerald-200 bg-emerald-50 text-emerald-800",
  BUSINESS_IDENTITY_VERIFIED: "border-emerald-200 bg-emerald-50 text-emerald-800",
  PLATFORM_ONBOARDING_INCOMPLETE: "border-sky-200 bg-sky-50 text-sky-800",
  CLAIMED_IDENTITY_PENDING: "border-amber-200 bg-amber-50 text-amber-800",
  UNCLAIMED: "border-slate-200 bg-slate-50 text-slate-600",
  RESTRICTED: "border-amber-200 bg-amber-50 text-amber-800",
  UNDER_REVIEW: "border-amber-200 bg-amber-50 text-amber-800",
  SUSPENDED: "border-rose-200 bg-rose-50 text-rose-700",
  REMOVED: "border-rose-200 bg-rose-50 text-rose-700",
};

export const SEVERITY_TONES = {
  critical: "border-rose-200 bg-rose-50 text-rose-700",
  high: "border-amber-200 bg-amber-50 text-amber-800",
  urgent: "border-rose-200 bg-rose-50 text-rose-700",
  medium: "border-sky-200 bg-sky-50 text-sky-800",
  standard: "border-slate-200 bg-slate-50 text-slate-700",
  normal: "border-slate-200 bg-slate-50 text-slate-700",
  low: "border-slate-200 bg-slate-50 text-slate-600",
};

export const ALERT_TONES = {
  critical: "border-rose-200 bg-rose-50 text-rose-800",
  high: "border-amber-200 bg-amber-50 text-amber-800",
  medium: "border-sky-200 bg-sky-50 text-sky-800",
  low: "border-slate-200 bg-slate-50 text-slate-600",
};

export const EXTERNAL_ACTION_NOTE = "ACTION REQUIRED OUTSIDE CONFIRMPH";

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------
export function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
}

export function fmtDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-PH", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
export const plGet = (path, config) => api.get(`${PL}${path}`, config).then((r) => r.data);
export const plPost = (path, body) => api.post(`${PL}${path}`, body).then((r) => r.data);
export const plPatch = (path, body) => api.patch(`${PL}${path}`, body).then((r) => r.data);
export const plPut = (path, body) => api.put(`${PL}${path}`, body).then((r) => r.data);

export function legalError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return err?.message || "Something went wrong.";
}

/** Download a CSV export through the authenticated client. */
export async function downloadExport(key) {
  const res = await api.get(`${PL}/exports/${key}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `confirmph-${key.replace(/_/g, "-")}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
